import React, { useState, useEffect, useRef } from 'react';
import { useInView, useReducedMotion, animate } from 'framer-motion';
import {
  Code2, Database, Brain, Server, Globe,
  Wrench, GitBranch, Smartphone
} from 'lucide-react';
import TechnologyMarquee from '../components/TechnologyMarquee';
import OrbitCarousel from '../components/ui/orbiting-carousel-with-animated-icons';
import { Section, SectionHeader } from '../components/ui/Section';
import { Card } from '../components/ui/Card';
import { Reveal } from '../components/ui/Reveal';
import {
  SiReact, SiTypescript, SiNextdotjs, SiNodedotjs, SiPython,
  SiMongodb, SiPostgresql, SiDocker, SiGit, SiGithub,
  SiJavascript, SiHtml5, SiCss3, SiTailwindcss, SiBootstrap,
  SiPhp, SiGo, SiDjango, SiFlask, 
  SiMysql, SiAmazonec2, SiAmazons3, SiAmazonsimpleemailservice, SiGithubactions,
  SiNestjs, SiAmazoneks, SiAmazonwebservices, SiTensorflow
} from 'react-icons/si';
import { TbBrandReactNative } from "react-icons/tb";

const CountUp = ({ to, suffix = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduceMotion) {
      setValue(to);
      return;
    }
    const controls = animate(0, to, {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setValue(Math.round(v))
    });
    return () => controls.stop();
  }, [inView, to, reduceMotion]);

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  );
};

export default function Skill() {
  const [activeFilter, setActiveFilter] = useState('all');

  const skillsData = [
    { name: 'React', category: 'frontend', IconComponent: SiReact, experience: '1+ years', description: 'Component-based UI library' },
    { name: 'TypeScript', category: 'frontend', IconComponent: SiTypescript, experience: '1+ years', description: 'Typed JavaScript at scale' },
    { name: 'Next.js', category: 'frontend', IconComponent: SiNextdotjs, experience: '0.4+ years', description: 'Full-stack React framework' },
    { name: 'JavaScript', category: 'frontend', IconComponent: SiJavascript, experience: '2+ years', description: 'Dynamic programming language' },
    { name: 'HTML5', category: 'frontend', IconComponent: SiHtml5, experience: '3+ years', description: 'Semantic markup language' },
    { name: 'CSS3', category: 'frontend', IconComponent: SiCss3, experience: '3+ years', description: 'Styling and layout' },
    { name: 'Tailwind CSS', category: 'frontend', IconComponent: SiTailwindcss, experience: '1+ years', description: 'Utility-first CSS framework' },
    { name: 'Bootstrap', category: 'frontend', IconComponent: SiBootstrap, experience: '2+ years', description: 'CSS framework' },
    { name: 'Node.js', category: 'backend', IconComponent: SiNodedotjs, experience: '1+ years', description: 'JavaScript runtime for servers' },
    { name: 'NestJS', category: 'backend', IconComponent: SiNestjs, experience: '1+ years', description: 'Progressive Node.js framework' },
    { name: 'Python', category: 'backend', IconComponent: SiPython, experience: '3+ years', description: 'Versatile programming language' },
    { name: 'Django', category: 'backend', IconComponent: SiDjango, experience: '1.5+ years', description: 'Python web framework' },
    { name: 'Flask', category: 'backend', IconComponent: SiFlask, experience: '1+ years', description: 'Lightweight Python framework' },
    { name: 'PHP', category: 'backend', IconComponent: SiPhp, experience: '0.5+ years', description: 'Server-side scripting language' },
    { name: 'Go', category: 'backend', IconComponent: SiGo, experience: '0.3+ years', description: 'Modern system language' },
    { name: 'MongoDB', category: 'database', IconComponent: SiMongodb, experience: '1+ years', description: 'NoSQL document database' },
    { name: 'PostgreSQL', category: 'database', IconComponent: SiPostgresql, experience: '1+ years', description: 'Advanced relational database' },
    { name: 'MySQL', category: 'database', IconComponent: SiMysql, experience: '1.5+ years', description: 'Popular relational database' },
    { name: 'React Native', category: 'mobile', IconComponent: TbBrandReactNative, experience: '0.5+ years', description: 'Cross-platform mobile framework' },
    { name: 'Git', category: 'tools', IconComponent: SiGit, experience: '2+ years', description: 'Distributed version control system' },
    { name: 'GitHub', category: 'tools', IconComponent: SiGithub, experience: '2+ years', description: 'Code hosting platform' },
    { name: 'Docker', category: 'devops', IconComponent: SiDocker, experience: '1+ years', description: 'Containerization platform' },
    { name: 'AWS EC2', category: 'devops', IconComponent: SiAmazonec2, experience: '1+ years', description: 'Elastic Compute Cloud' },
    { name: 'AWS S3', category: 'devops', IconComponent: SiAmazons3, experience: '1+ years', description: 'Simple Storage Service' },
    { name: 'AWS SES', category: 'devops', IconComponent: SiAmazonsimpleemailservice, experience: '1+ years', description: 'Simple Email Service' },
    { name: 'AWS ECR', category: 'devops', IconComponent: SiAmazonwebservices, experience: '1+ years', description: 'Elastic Container Registry' },
    { name: 'AWS EKS', category: 'devops', IconComponent: SiAmazoneks, experience: '1+ years', description: 'Elastic Kubernetes Service' },
    { name: 'GitHub Actions', category: 'devops', IconComponent: SiGithubactions, experience: '1+ years', description: 'Automated CI/CD workflows' },
    { name: 'TensorFlow', category: 'ai', IconComponent: SiTensorflow, experience: '1+ year', description: 'Machine learning framework' },
    { name: 'RPA (UiPath)', category: 'ai', IconComponent: Brain, experience: '0.8+ years', description: 'Robotic Process Automation' },
  ];

  const categories = [
    { id: 'all', name: 'All Skills', icon: Globe },
    { id: 'frontend', name: 'Frontend', icon: Code2 },
    { id: 'backend', name: 'Backend', icon: Server },
    { id: 'database', name: 'Database', icon: Database },
    { id: 'mobile', name: 'Mobile', icon: Smartphone },
    { id: 'tools', name: 'Tools', icon: Wrench },
    { id: 'devops', name: 'DevOps', icon: GitBranch },
    { id: 'ai', name: 'AI/ML', icon: Brain },
  ];

  const filteredSkills = activeFilter === 'all'
    ? skillsData
    : skillsData.filter(skill => skill.category === activeFilter);

  const laneItems = (cats) =>
    skillsData
      .filter(s => cats.includes(s.category))
      .map(({ name, IconComponent }) => ({ name, IconComponent }));

  const lanes = [
    { items: laneItems(['frontend', 'backend', 'devops']), variant: 'display', direction: 'left', speed: 80 },
    { items: laneItems(['database', 'ai', 'tools', 'mobile']), variant: 'muted', direction: 'right', speed: 48 }
  ];

  return (
    <Section id="skill">
      <SectionHeader
        title="Skills & Technologies"
        subtitle="Full stack technologies, machine learning frameworks, and engineering tools."
      />

      {/* Category Filter Pills */}
      <div className="flex flex-wrap justify-center gap-1.5 mb-8">
        {categories.map((category) => {
          const Icon = category.icon;
          const isActive = activeFilter === category.id;
          const count = category.id === 'all' ? skillsData.length : skillsData.filter(s => s.category === category.id).length;

          return (
            <button
              key={category.id}
              type="button"
              onClick={() => setActiveFilter(category.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                isActive
                  ? 'bg-[var(--fg-primary)] text-[var(--bg-canvas)]'
                  : 'bg-[var(--bg-elevated)] text-[var(--fg-secondary)] hover:text-[var(--fg-primary)] hover:bg-[var(--bg-hover)] shadow-[var(--border-ring)]'
              }`}
            >
              <Icon size={14} />
              <span>{category.name}</span>
              <span className="opacity-70 font-mono">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Orbiting Skills Carousel */}
      <div className="my-6">
        <OrbitCarousel
          items={filteredSkills.map((skill) => ({
            id: skill.name,
            name: skill.name,
            icon: skill.IconComponent,
            description: skill.description,
          }))}
        />
      </div>

      {/* Kinetic Technology Marquee */}
      <div className="py-8 my-8 border-y border-[var(--bg-recessed)] space-y-4">
        {lanes.map((lane, index) => (
          <TechnologyMarquee
            key={index}
            items={lane.items}
            variant={lane.variant}
            direction={lane.direction}
            speed={lane.speed}
          />
        ))}
      </div>

      {/* Stats band — 4+ years is the credential; the other two support it. */}
      <Reveal className="mt-12">
        <Card className="p-5 sm:p-8">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 sm:gap-8 sm:items-end">
            <div className="sm:pr-4 sm:border-r sm:border-[var(--bg-recessed)]">
              <p className="t-h2 text-[var(--fg-primary)] mb-1">
                <CountUp to={4} suffix="+" />
              </p>
              <p className="t-label text-[var(--fg-primary)] mb-1">Years coding</p>
              <p className="text-xs text-[var(--fg-secondary)] leading-relaxed">
                Building production software since 2022
              </p>
            </div>

            <div className="text-left sm:text-center">
              <p className="text-2xl font-semibold tracking-tight text-[var(--fg-primary)] mb-1 sm:text-3xl">
                <CountUp to={skillsData.length} suffix="+" />
              </p>
              <p className="text-xs text-[var(--fg-secondary)] font-medium">Technologies</p>
            </div>

            <div className="text-left sm:text-center">
              <p className="text-2xl font-semibold tracking-tight text-[var(--fg-primary)] mb-1 sm:text-3xl">
                ∞
              </p>
              <p className="text-xs text-[var(--fg-secondary)] font-medium">Continuous learning</p>
            </div>
          </div>
        </Card>
      </Reveal>
    </Section>
  );
}